/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { ComponentChildren, Ref, JSX } from 'preact';
import { forwardRef } from 'preact/compat';
import { pick } from 'ramda';

import { dimensionInterpolations, DimensionProps } from '#utils/UNSAFE_interpolations/dimensions';
import { paddingInterpolations, PaddingProps } from '#utils/UNSAFE_interpolations/padding';
import { colorInterpolations, ColorProps } from '#utils/UNSAFE_interpolations/colors';
import { AriaProps, ariaInterpolations } from '#utils/UNSAFE_interpolations/aria';
import { mergeInterpolations } from '#utils/UNSAFE_mergeInterpolations';
import { multiVariantStyles } from './themes/StyledCardStyles.css';
import { classNames } from '#utils/UNSAFE_classNames';
import { TestIdProps, useTestId } from '#hooks/UNSAFE_useTestId';
import { Role } from '#utils/UNSAFE_roles';
import type { UnpackSignals } from '@oracle/oraclejet-internal-utilities/attributeUtils';
import { useCheckboxRadioContext } from '#PRIVATE_CheckboxRadioField/CheckboxRadioContext';
import { getClientHints } from '#utils/PRIVATE_clientHints';

type IntrinsicProps = Pick<UnpackSignals<JSX.IntrinsicElements['div']>, EventHandlers>;

type EventHandlers = (typeof eventHandlers)[number];

type StyleProps = DimensionProps & PaddingProps & ColorProps & AriaProps;
type Props = StyleProps &
  IntrinsicProps &
  TestIdProps & {
    children?: ComponentChildren;
    /**
     * Specifies if the card uses the pointer cursor.
     */
    cursor?: 'pointer' | 'arrow';
    /**
     * Specifies the card element display type.
     */
    display?: 'inlineBlock' | 'inlineFlex';
    /**
     * Specifies if the card is in an active state.
     */
    isActive?: boolean;
    /**
     * Specifies if the card is in a disabled state.
     */
    isDisabled?: boolean;
    /**
     * Specifies if the card is in a hover state.
     */
    isHover?: boolean;
    /**
     * Specifies if the card is a read-only option.
     */
    isReadonly?: boolean;
    /**
     * Specifies if the card as selected within a collection.
     */
    isSelected?: boolean;
    /**
     * Specifies the parent component variant
     */
    parentComponentVariant?: (typeof parentComponentVariants)[number];
    /**
     * Specifies aria role for the card element.
     */
    role?: Role;
    /**
     * Specifies the chroming of the card element
     */
    styleVariant?: 'danger';
  } & (
    | {
        /**
         * Specifies if the card has a focused input within.
         */
        isFocusWithin?: boolean;
        tabIndex?: never;
      }
    | {
        /**
         * Specifies the tab index of the card element.
         */
        tabIndex?: number;
        isFocusWithin?: never;
      }
  ) &
  Pick<UnpackSignals<JSX.AriaAttributes>, 'aria-describedby'>;

const parentComponentVariants = [
  'default',
  'actionCard',
  'selectionCard',
  'richSelectionItemCard'
] as const;

const eventHandlers = [
  'onBlur',
  'onClick',
  'onContextMenu',
  'onFocus',
  'onKeyDown',
  'onKeyUp',
  'onMouseDown',
  'onMouseEnter',
  'onMouseLeave',
  'onMouseUp',
  'onPointerCancel',
  'onPointerDown',
  'onPointerOut',
  'onPointerUp',
  'onTouchCancel',
  'onTouchEnd',
  'onTouchStart'
] as const;

const interpolations = [
  ...Object.values(dimensionInterpolations),
  ...Object.values(colorInterpolations),
  ...Object.values(paddingInterpolations),
  ...Object.values(ariaInterpolations)
];

const mergedInterpolations = mergeInterpolations<Props>(interpolations);

/**
 * A StyledCard is a simple container that renders a group of related information in a styled rectangular area.
 * Width, height, padding, and background color are set through props.
 * This component is used by Card, SelectionCard, ActionCard, and RichSelectionItem.
 */
const StyledCard = forwardRef(
  (
    {
      ['aria-describedby']: ariaDescribedBy,
      children,
      cursor,
      display = 'inlineBlock',
      isActive,
      isDisabled,
      isFocusWithin,
      isHover,
      isReadonly,
      isSelected,
      parentComponentVariant = 'default',
      role,
      styleVariant,
      tabIndex,
      testId,
      ...rest
    }: Props,
    ref: Ref<HTMLDivElement>
  ) => {
    const testIdProps = useTestId(testId);

    // we want to pick all the event handlers only to spread over the div element
    const eventHandlersProps = pick(eventHandlers, rest);

    const {
      class: mergedClasses,
      ariaLabel,
      ariaLabelledBy,
      ...mergedStyles
    } = mergedInterpolations(rest);

    /**
     * JET-76970
     * Safari's `:focus-visible` is applied only during keyboard focus (e.g., Tab).
     * However, when a child of a card receives focus via arrow navigation, Safari
     * ignores `:focus-visible` even if it would match, and it only shows a focus
     * outline at navigation endpoints.
     *
     * For consistency:
     * - For Safari, add a class that forces the ring whenever focus is inside the
     *   card during keyboard navigation.
     * - For other browsers or readonly cards, rely on native `:focus-visible` selectors.
     *
     * Known issue:
     * - Because of the timeout in useCollectionFocusRing, a long press on a item can
     *   sometimes trigger a focus ring, whereas a normal click does not.
     */
    const { isFocusRingShown } = useCheckboxRadioContext();
    const isSafari = getClientHints().browser === 'safari';
    const focusWithRing = isFocusWithin && isFocusRingShown;
    const isFocusRingSelector = (!isSafari || isReadonly) && focusWithRing;
    const isForcedFocusRing = isSafari && focusWithRing;

    const variantClasses = multiVariantStyles({
      active: isActive ? 'isActive' : undefined,
      cursor,
      display,
      disabled: isDisabled ? 'isDisabled' : undefined,
      focusRing: isForcedFocusRing
        ? 'isForcedFocusRing'
        : isFocusRingSelector
        ? 'isFocusRingSelector'
        : undefined,
      hover: isHover ? 'isHover' : 'notHover',
      readonly: isReadonly ? 'isReadonly' : undefined,
      parentComponentVariant,
      selected: isSelected ? 'isSelected' : 'notSelected',
      styleVariant
    });

    const classes = classNames([variantClasses, mergedClasses]);

    return (
      <div
        {...eventHandlersProps}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        className={classes}
        tabIndex={tabIndex}
        ref={ref}
        role={role}
        style={mergedStyles}
        {...testIdProps}>
        {children}
      </div>
    );
  }
);

export { StyledCard, parentComponentVariants };
